import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, Navigate } from 'react-router-dom';
import * as Contracts from '@displaycms/contracts';

const LoginInput = Contracts.LoginInput;
type LoginInput = Contracts.LoginInput;

import { ApiError } from '@/api/client';
import { useAuth } from '@/auth/auth-context';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Logo } from '@/components/logo';

/**
 * Login form for the admin SPA — split-screen layout (UI Polish Wave 1 T5).
 *
 * Left panel (45% width, hidden below lg breakpoint): slate-900 brand panel
 * with single radial-blur amber orb, brand headline, tagline, and system
 * status indicator.
 *
 * Right panel (55%): card-less centred form with amber focus ring (--ring
 * token). Form logic is unchanged from the original implementation.
 *
 * - Validates against the shared `LoginInput` zod schema from
 *   `@displaycms/contracts` (no schema drift with the server).
 * - On success, navigates to the `from` location the route guard captured
 *   (defaults to `/`).
 * - On failure, displays the server's i18n message translated through the
 *   `auth.errors.*` namespace; if the message isn't a known key, falls back
 *   to a generic invalid-credentials string.
 */
interface LocationState {
  from?: { pathname?: string };
}

interface LoginFields {
  username: string;
  password: string;
  mfa_code: string;
}

export function LoginPage(): JSX.Element {
  const { t } = useTranslation(['auth', 'common', 'errors']);
  const navigate = useNavigate();
  const location = useLocation();
  const { login, status } = useAuth();

  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // P7 MFA: once the server answers `mfa_required`, reveal the 6-digit code
  // field and keep it visible for retries.
  const [mfaRequired, setMfaRequired] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFields>({
    defaultValues: { username: '', password: '', mfa_code: '' },
  });

  // If already authenticated, bounce to the dashboard.
  if (status === 'authenticated') {
    return <Navigate to="/" replace />;
  }

  const fromState = (location.state ?? {}) as LocationState;
  const redirectTo = fromState.from?.pathname ?? '/';

  function translateServerError(message: string): string {
    const candidate = t(`errors.${message}`, {
      ns: 'auth',
      defaultValue: '',
    });
    if (candidate) return candidate;
    return t('invalid_credentials', { ns: 'auth' });
  }

  async function onSubmit(values: LoginFields): Promise<void> {
    setSubmitError(null);
    // Only send mfa_code once the server has asked for it AND it is non-empty
    // (LoginInput requires exactly 6 digits when present).
    const payload = {
      username: values.username,
      password: values.password,
      ...(mfaRequired && values.mfa_code.trim().length > 0
        ? { mfa_code: values.mfa_code.trim() }
        : {}),
    };
    const parsed = LoginInput.safeParse(payload);
    if (!parsed.success) {
      setSubmitError(
        mfaRequired && payload.mfa_code === undefined
          ? t('mfa_code_required', { ns: 'auth', defaultValue: 'Enter your 6-digit code.' })
          : t('invalid_credentials', { ns: 'auth' }),
      );
      return;
    }
    setSubmitting(true);
    try {
      await login(parsed.data);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      if (err instanceof ApiError && (err.code === 'mfa_required' || err.code === 'invalid_mfa_code')) {
        // Reveal (or keep) the code field. Show a specific message for a wrong
        // code; the first prompt is neutral.
        setMfaRequired(true);
        setSubmitError(
          err.code === 'invalid_mfa_code'
            ? t('invalid_mfa_code', { ns: 'auth', defaultValue: 'Invalid verification code.' })
            : t('mfa_required', { ns: 'auth', defaultValue: 'Enter the code from your authenticator app.' }),
        );
      } else if (err instanceof ApiError) {
        setSubmitError(translateServerError(err.message));
      } else {
        setSubmitError(t('invalid_credentials', { ns: 'auth' }));
      }
    } finally {
      setSubmitting(false);
    }
  }

  // Manual zod-backed per-field validators — keeps us off `@hookform/resolvers`
  // while still preventing empty submits.
  const usernameValidator = (value: string): true | string => {
    const r = LoginInput.shape.username.safeParse(value);
    if (r.success) return true;
    return t('username_required', { ns: 'auth' });
  };
  const passwordValidator = (value: string): true | string => {
    if (value.length === 0) return t('password_required', { ns: 'auth' });
    const r = LoginInput.shape.password.safeParse(value);
    if (r.success) return true;
    return t('password_too_short', { ns: 'auth' });
  };

  return (
    <div className="min-h-screen flex">
      {/* Left brand panel — hidden below lg breakpoint */}
      <aside
        className="hidden lg:flex lg:w-[45%] relative overflow-hidden bg-[hsl(var(--sidebar))] text-[hsl(var(--sidebar-foreground))] flex-col justify-between p-10"
        aria-hidden="true"
      >
        {/* Radial-blur orb — bottom-right */}
        <div
          className="absolute -bottom-20 -right-20 w-[400px] h-[400px] rounded-full bg-[hsl(var(--primary))]/30 blur-[80px]"
          aria-hidden="true"
        />

        {/* Top: logo + wordmark */}
        <div className="relative z-10">
          <Logo />
        </div>

        {/* Centre: brand headline + tagline */}
        <div className="relative z-10 -mt-16">
          <h1 className="text-4xl font-bold leading-tight text-white">
            {t('brand_headline', { ns: 'auth' })}
          </h1>
          <p className="mt-4 text-lg text-white/70">
            {t('brand_tagline', { ns: 'auth' })}
          </p>
        </div>

        {/* Bottom: copyright + system status */}
        <div className="relative z-10 flex items-center justify-between text-xs text-white/50">
          <span>© {new Date().getFullYear()} displaycms</span>
          <div className="flex items-center gap-1.5">
            <span
              className="inline-block h-1.5 w-1.5 rounded-full bg-[hsl(var(--primary))] animate-pulse"
              aria-hidden="true"
            />
            <span>{t('system_operational', { ns: 'auth' })}</span>
          </div>
        </div>
      </aside>

      {/* Right form panel */}
      <main className="flex-1 flex items-center justify-center px-6 py-12 bg-background">
        <div className="w-full max-w-md">
          {/* Wordmark — visible on mobile only (brand panel is hidden < lg) */}
          <div className="lg:hidden mb-8 text-center">
            <div className="text-2xl font-semibold tracking-tight">displaycms</div>
          </div>

          <h2 className="text-2xl font-bold text-foreground">
            {t('login_title', { ns: 'auth' })}
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            {t('login_subtitle', { ns: 'auth' })}
          </p>

          {submitError && (
            <p
              role="alert"
              data-testid="login-error"
              className="mt-6 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
            >
              {submitError}
            </p>
          )}

          <form
            onSubmit={(e) => {
              void handleSubmit(onSubmit)(e);
            }}
            noValidate
            className="mt-6 flex flex-col gap-4"
          >
            <div className="flex flex-col gap-1">
              <label htmlFor="username" className="text-sm font-medium text-foreground">
                {t('username', { ns: 'auth' })}
              </label>
              <Input
                id="username"
                type="text"
                autoComplete="username"
                autoFocus
                placeholder={t('username_placeholder', { ns: 'auth' })}
                aria-invalid={errors.username ? 'true' : 'false'}
                {...register('username', { validate: usernameValidator })}
              />
              {errors.username && (
                <p className="text-sm text-destructive" role="alert">
                  {errors.username.message}
                </p>
              )}
            </div>

            <div className="flex flex-col gap-1">
              <label htmlFor="password" className="text-sm font-medium text-foreground">
                {t('password', { ns: 'auth' })}
              </label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                placeholder={t('password_placeholder', { ns: 'auth' })}
                aria-invalid={errors.password ? 'true' : 'false'}
                {...register('password', { validate: passwordValidator })}
              />
              {errors.password && (
                <p className="text-sm text-destructive" role="alert">
                  {errors.password.message}
                </p>
              )}
            </div>

            {mfaRequired && (
              <div className="flex flex-col gap-1">
                <label htmlFor="mfa_code" className="text-sm font-medium text-foreground">
                  {t('mfa_code_label', { ns: 'auth', defaultValue: 'Verification code' })}
                </label>
                <Input
                  id="mfa_code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  placeholder="123456"
                  autoFocus
                  aria-invalid={errors.mfa_code ? 'true' : 'false'}
                  {...register('mfa_code')}
                />
                <p className="text-xs text-muted-foreground">
                  {t('mfa_code_hint', {
                    ns: 'auth',
                    defaultValue: 'Enter the 6-digit code from your authenticator app.',
                  })}
                </p>
              </div>
            )}

            <Button type="submit" disabled={submitting}>
              {submitting
                ? t('signing_in', { ns: 'auth' })
                : t('sign_in', { ns: 'auth' })}
            </Button>
          </form>
        </div>
      </main>
    </div>
  );
}

export default LoginPage;
